import { IInstructionItemJson } from '@scale/shared/types/instructionitem';
import { AuditBatchJson, IAttemptAuditJson } from '@scale/shared/types/speedAudit';
import {
  SpeedAuditResult,
  SubtaskAssignment,
} from 'react-scale/components/audits/SpeedAudit/types';
import { getInitialResult } from 'react-scale/components/audits/SpeedAudit/utils';
import { create } from 'zustand';
import { shallow } from 'zustand/shallow';

export type AuditDto = {
  projectId: string;
  attemptIds: string[];
  projectInstrItems: IInstructionItemJson[];
  auditBatch: AuditBatchJson;
  attemptAudits: IAttemptAuditJson[];
  auditGoal?: string;
};

type AuditState = {
  loadedAudit: AuditDto | undefined;
  subTasks: Record<string, SubtaskAssignment>;
  auditToTaskMap: Record<string, string>;
  auditResults: Record<string, SpeedAuditResult | undefined>;
  actions: AuditActions;
  openChatBulkAuditResults: boolean;
};

type AuditActions = {
  setLoadedAudit: (loadedAudit: AuditDto) => void;
  setSubtasks: (subTasks: SubtaskAssignment[]) => void;
  clearLoadedAudit: () => void;
  setAuditToTaskMap: (auditToTaskMap: Record<string, string>) => void;
  setAuditResults: (objectId: string, auditResult: SpeedAuditResult | undefined) => void;
  setOpenChatBulkAuditResults: (openChatBulkAuditResults: boolean) => void;
  deleteAuditResults: (objectId: string) => void;
  resetAuditResults: () => void;
  reset: () => void;
};

const useAuditStore = create<AuditState>(set => ({
  auditingProjectId: undefined,
  loadedAudit: undefined,
  auditToTaskMap: {},
  subTasks: {},
  auditResults: {},
  openChatBulkAuditResults: false,
  actions: {
    setLoadedAudit: loadedAudit => set(() => ({ loadedAudit })),
    setSubtasks: subTasks =>
      set(() => ({
        subTasks: subTasks.reduce(
          (acc, subTask) => ({ ...acc, [subTask.auditLookup.id ?? '']: subTask }),
          {},
        ),
        auditResults: subTasks.reduce(
          (acc, subtask) => ({
            ...acc,
            [subtask.auditLookup.id ?? '']: getInitialResult(subtask),
          }),
          {},
        ),
      })),
    setAuditResults: (objectId, auditResult) =>
      set(state => ({
        auditResults: { ...state.auditResults, [objectId]: auditResult },
      })),
    deleteAuditResults: objectId => {
      set(state => {
        const auditResults = state.auditResults;
        delete auditResults[objectId];
        return {
          auditResults,
        };
      });
    },
    resetAuditResults: () => {
      set(() => ({
        auditResults: {},
      }));
    },
    clearLoadedAudit: () => set({ loadedAudit: undefined }),
    setAuditToTaskMap: auditToTaskMap => set(() => ({ auditToTaskMap })),
    reset: () =>
      set({
        loadedAudit: undefined,
        subTasks: {},
        auditResults: {},
      }),
    setOpenChatBulkAuditResults: openChatBulkAuditResults =>
      set(() => ({ openChatBulkAuditResults })),
  },
}));

export const useLoadedAudit = () => useAuditStore(state => state.loadedAudit, shallow);
export const useLoadedSubtasks = () => useAuditStore(state => state.subTasks, shallow);
export const useAuditToTaskMap = () => useAuditStore(state => state.auditToTaskMap, shallow);
export const useSpeedAuditResults = () => useAuditStore(state => state.auditResults);
export const useOpenChatBulkAuditResults = () =>
  useAuditStore(state => state.openChatBulkAuditResults);

export const useAuditActions = () => useAuditStore(state => state.actions);
